import type { IOrganizationWithStats } from '#backend/common/organization-types';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { formatCNPJ } from '@brazilian-utils/brazilian-utils';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { EnhancedLoadingService } from '../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../shared/services/smart-orpc-client.service';
import { createNestedOrder } from '../../shared/utils/create-nested-order';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TypeSafeMatCellDef } from '../../type-safe-mat-cell-def.directive';

@Component({
  selector: 'app-organizations',
  imports: [
    AsyncPipe,
    DatePipe,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RouterModule,
    TypeSafeMatCellDef,
  ],
  templateUrl: './organizations.component.html',
  styleUrl: './organizations.component.scss',
})
export class OrganizationsComponent implements AfterViewInit, OnDestroy {
  orpc = inject(SmartOrpcClientService);
  router = inject(Router);
  loadingService = inject(EnhancedLoadingService);

  searchControl = new FormControl('');
  @ViewChild(MatPaginator) declare paginator: MatPaginator;
  abortController?: AbortController;

  formatCNPJ = formatCNPJ;

  dataSource = new MatTableDataSource<IOrganizationWithStats>([
    {
      id: 0,
      cnpj: '',
      name: 'Carregando...',
      createdBy: '',
      lastModifiedBy: null,
      activeContractsCount: 0,
      inactiveContractsCount: 0,
    },
  ]);

  displayedColumns: string[] = [
    'name',
    'cnpj',
    'createdDate',
    'activeContractsCount',
    'inactiveContractsCount',
    'actions',
  ];
  pageIndex: unknown;
  private snackBar = inject(MatSnackBar);

  ngOnDestroy() {
    this.abortController?.abort();
  }
  ngAfterViewInit(): void {
    this.search();
    // Debounce na busca por texto com feedback visual
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.loadingService.show('input');

        this.search().then(() => {
          this.loadingService.hide('input');
        });
      });
  }

  async search(pageIndex = 0) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.pageIndex = pageIndex;
    await this.orpc.client.organization
      .listWithStats(
        {
          searchText: this.searchControl.value || undefined,
          cursor: pageIndex + 1,
          limit: this.paginator.pageSize,
          orderBy: this.dataSource.sort?.active
            ? createNestedOrder(this.dataSource.sort)
            : undefined,
        },
        { signal: this.abortController.signal },
      )
      .then(({ items, total }) => {
        this.dataSource.data = items;
        this.paginator.length = total;
      });
  }

  alert(message: string, duration = 5000) {
    this.snackBar.open(message, 'Fechar', { duration });
  }

  clearText() {
    this.searchControl.reset();
  }

  navigateToNewOrganization() {
    this.router.navigate(['admin', 'organizations', 'new']);
  }
  navigateToOrganizationDetails(id: number) {
    this.router.navigate(['admin', 'organizations', 'details', id]);
  }

  navigateToCreateContract(organizationId: number) {
    if (organizationId) {
      this.router.navigate(['/admin/contracts/new'], {
        queryParams: { organizationId: organizationId },
      });
    }
  }
}
